/**
 * Cancha Bundle — Contents block + Product preview modal
 * Vanilla JS · No dependencies · Self-contained
 *
 * Loaded ONLY by the bundle-contents block. The main bundle-builder
 * loads its own larger script (cancha-bundle.js); pages that use only
 * the contents block save the parse cost of the full builder runtime.
 */
'use strict';

(function () {
  if (window.__canchaContentsLoaded) return;
  window.__canchaContentsLoaded = true;

  // ── Utilities ─────────────────────────────────────────────────
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
  const fmt = (cents) => {
    const fmtStr = window.Shopify?.money_format || '${{amount}}';
    const amt = (cents / 100).toFixed(2);
    return fmtStr
      .replace('{{amount}}', amt)
      .replace('{{amount_no_decimals}}', Math.floor(cents / 100))
      .replace('{{amount_with_comma_separator}}', amt.replace('.', ','));
  };

  // ── Product preview modal ─────────────────────────────────────

  function initProductPreviewModal() {
    document.addEventListener('click', async (e) => {
      const trigger = e.target.closest('[data-cb-show-product]');
      if (trigger) {
        e.preventDefault();
        const handle = trigger.dataset.cbShowProduct;
        const url = trigger.dataset.productUrl || `/products/${handle}`;
        await openProductModal(handle, url);
        return;
      }
      if (e.target.closest('[data-cb-product-modal-close]')) {
        closeProductModal();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const pm = $('[data-cb-product-modal]:not([hidden])');
      if (pm) closeProductModal();
    });
  }

  async function openProductModal(handle, url) {
    const modal = $('[data-cb-product-modal]');
    if (!modal) return;
    // Teleport to <body> so the modal escapes any clipped/transformed/sticky
    // ancestor (e.g. .product-info__block-item:has(.cb-contents) which sets
    // overflow:hidden, or theme-editor iframe wrappers). Without this, the
    // modal's `position: fixed; inset: 0` resolves against the wrong
    // containing block on mobile, leaving content visually shifted left.
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }
    const body = $('[data-cb-product-modal-body]', modal);
    body.innerHTML = '<div class="cb-product-modal__loading">Loading…</div>';
    modal.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';

    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) throw new Error('Could not load product');
      const product = await res.json();
      body.innerHTML = renderProductModal(product, url);
    } catch (err) {
      body.innerHTML = `<div class="cb-product-modal__error">${err.message || 'Could not load product'}</div>`;
    }
  }

  function closeProductModal() {
    const modal = $('[data-cb-product-modal]');
    if (!modal) return;
    modal.setAttribute('hidden', '');
    document.body.style.overflow = '';
  }

  function renderProductModal(product, url) {
    const images = (product.media || product.images || []).slice(0, 6);
    const imageUrl = (img) =>
      typeof img === 'string'
        ? img
        : img?.src || img?.preview_image?.src || '';
    const mainImage = imageUrl(images[0]) || product.featured_image || '';
    const priceFmt = fmt(product.price);
    const compareFmt = product.compare_at_price && product.compare_at_price > product.price
      ? `<span class="cb-product-modal__price-was">${fmt(product.compare_at_price)}</span>`
      : '';

    const description = product.description
      ? `<div class="cb-product-modal__desc">${product.description}</div>`
      : '';

    const thumbs = images.length > 1
      ? `<div class="cb-product-modal__thumbs">${images.map((img) => `
          <button type="button" class="cb-product-modal__thumb" data-cb-thumb-src="${imageUrl(img)}">
            <img src="${imageUrl(img)}" alt="" loading="lazy">
          </button>
        `).join('')}</div>`
      : '';

    return `
      <div class="cb-product-modal__layout">
        <div class="cb-product-modal__media">
          <div class="cb-product-modal__main-img-wrap">
            <img class="cb-product-modal__main-img" src="${mainImage}" alt="${product.title}">
          </div>
          ${thumbs}
        </div>
        <div class="cb-product-modal__info">
          <h2 class="cb-product-modal__title">${product.title}</h2>
          <div class="cb-product-modal__price-row">
            <span class="cb-product-modal__price">${priceFmt}</span>
            ${compareFmt}
          </div>
          ${description}
          <a class="cb-btn cb-btn--ghost cb-product-modal__view-full" href="${url}">View full product</a>
        </div>
      </div>
    `;
  }

  // Thumbnail click → swap main image
  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-cb-thumb-src]');
    if (!thumb) return;
    const modal = thumb.closest('[data-cb-product-modal]');
    const main = modal?.querySelector('.cb-product-modal__main-img');
    if (main) main.src = thumb.dataset.cbThumbSrc;
  });

  // ── Slider — prev/next buttons + auto-hide at edges ───────────

  function initContentsScrollers() {
    $$('[data-cb-contents-scroller]').forEach((scroller) => {
      const grid = scroller.querySelector('[data-cb-contents-grid]');
      const prev = scroller.querySelector('[data-cb-contents-prev]');
      const next = scroller.querySelector('[data-cb-contents-next]');
      if (!grid) return;

      const updateNav = () => {
        const max = grid.scrollWidth - grid.clientWidth;
        const atStart = grid.scrollLeft <= 1;
        const atEnd = grid.scrollLeft >= max - 1;
        const overflows = max > 4;
        scroller.dataset.overflow = overflows ? '' : 'none';
        if (prev) prev.toggleAttribute('hidden', !overflows || atStart);
        if (next) next.toggleAttribute('hidden', !overflows || atEnd);
      };

      const scrollByAmount = (dir) => {
        const tile = grid.querySelector('.cb-contents__tile');
        const tileWidth = tile?.getBoundingClientRect().width ?? grid.clientWidth * 0.4;
        const gap = parseFloat(getComputedStyle(grid).gap) || 14;
        grid.scrollBy({ left: dir * (tileWidth + gap), behavior: 'smooth' });
      };

      prev?.addEventListener('click', () => scrollByAmount(-1));
      next?.addEventListener('click', () => scrollByAmount(1));
      grid.addEventListener('scroll', updateNav, { passive: true });
      window.addEventListener('resize', updateNav);
      requestAnimationFrame(updateNav);
      setTimeout(updateNav, 400);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initContentsScrollers();
      initProductPreviewModal();
    });
  } else {
    initContentsScrollers();
    initProductPreviewModal();
  }
})();
